/**
 * Drizzle query builders + query-key factory del dominio de cursos (Space).
 *
 * Consumo local-first: el árbol del curso (course/unit/lesson/block), la
 * inscripción, el progreso y las stats del alumno viven en PowerSync/WASQLite
 * y se leen offline. El CATÁLOGO público, en cambio, es autoritativo del
 * servidor (REST `/api/catalog`, guest-first) — ver `catalog.ts`.
 */

import {
  and,
  asc,
  block,
  count,
  course,
  enrollment,
  eq,
  isNull,
  leaderboard_entry,
  learner_stats,
  lesson,
  lesson_progress,
  type PowerSyncDrizzleDB,
  unit,
} from "@space/powersync";

/** Claves de caché del dominio de cursos (TanStack Query). */
export const courseQueryKeys = {
  all: ["courses"] as const,
  authored: (userId: string | undefined) => ["courses", "authored", userId] as const,
  tree: (courseId: string | undefined) => ["courses", "tree", courseId] as const,
  units: (courseId: string | undefined) => ["courses", "units", courseId] as const,
  lessons: (courseId: string | undefined) => ["courses", "lessons", courseId] as const,
  blocks: (lessonId: string | undefined) => ["courses", "blocks", lessonId] as const,
  enrollments: (userId: string | undefined) => ["courses", "enrollments", userId] as const,
  enrollment: (userId: string | undefined, courseId: string | undefined) =>
    ["courses", "enrollment", userId, courseId] as const,
  progress: (userId: string | undefined, courseId: string | undefined) =>
    ["courses", "progress", userId, courseId] as const,
  learnerStats: (userId: string | undefined) => ["courses", "learner-stats", userId] as const,
  certificates: (userId: string | undefined) => ["courses", "certificates", userId] as const,
  certificate: (userId: string | undefined, courseId: string | undefined) =>
    ["courses", "certificate", userId, courseId] as const,
  certificateById: (certId: string | undefined) =>
    ["courses", "certificate-by-id", certId] as const,
  /** Verificación PÚBLICA por id (REST, server autoritativo) — no PowerSync. */
  certificateVerification: (certId: string | undefined) =>
    ["certificates", "verify", certId] as const,
  reviewQueue: (userId: string | undefined) => ["courses", "review-queue", userId] as const,
  leaderboard: (leagueId: string | undefined) => ["courses", "leaderboard", leagueId] as const,
  leaderboardOptIn: (userId: string | undefined) =>
    ["courses", "leaderboard-opt-in", userId] as const,
  myOrganizations: () => ["courses", "my-organizations"] as const,
  /** Catálogo PÚBLICO (REST, server autoritativo) — no PowerSync. */
  catalogList: (q: string) => ["catalog", "list", q] as const,
  catalogCourse: (courseId: string) => ["catalog", "course", courseId] as const,
};

/** Cursos que el usuario AUTORA (incluye borradores). Para `/authoring`. */
export function getMyAuthoredCoursesQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select()
    .from(course)
    .where(and(eq(course.author_id, userId), isNull(course.deleted_at)))
    .orderBy(asc(course.updated_at));
}

/** Metadatos de un curso (autoría o catálogo local ya sincronizado). */
export function getCourseQuery(db: PowerSyncDrizzleDB, courseId: string) {
  return db.select().from(course).where(eq(course.id, courseId)).limit(1);
}

/** Unidades de un curso, ordenadas. */
export function getUnitsQuery(db: PowerSyncDrizzleDB, courseId: string) {
  return db
    .select()
    .from(unit)
    .where(and(eq(unit.course_id, courseId), isNull(unit.deleted_at)))
    .orderBy(asc(unit.position), asc(unit.created_at));
}

/** Lecciones de un curso, ordenadas (agrupables por `unit_id`). */
export function getLessonsQuery(db: PowerSyncDrizzleDB, courseId: string) {
  return db
    .select()
    .from(lesson)
    .where(and(eq(lesson.course_id, courseId), isNull(lesson.deleted_at)))
    .orderBy(asc(lesson.position), asc(lesson.created_at));
}

/** Bloques de una lección, ordenados. */
export function getBlocksQuery(db: PowerSyncDrizzleDB, lessonId: string) {
  return db
    .select()
    .from(block)
    .where(and(eq(block.lesson_id, lessonId), isNull(block.deleted_at)))
    .orderBy(asc(block.position), asc(block.created_at));
}

/** Inscripciones del usuario. */
export function getMyEnrollmentsQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select()
    .from(enrollment)
    .where(and(eq(enrollment.user_id, userId), isNull(enrollment.deleted_at)))
    .orderBy(asc(enrollment.created_at));
}

/** ¿Está el usuario inscrito en un curso? (fila única). */
export function getEnrollmentQuery(db: PowerSyncDrizzleDB, userId: string, courseId: string) {
  return db
    .select()
    .from(enrollment)
    .where(and(eq(enrollment.user_id, userId), eq(enrollment.course_id, courseId)))
    .limit(1);
}

/** Progreso del alumno en un curso (todas las lecciones tocadas). */
export function getCourseProgressQuery(db: PowerSyncDrizzleDB, userId: string, courseId: string) {
  return db
    .select()
    .from(lesson_progress)
    .where(and(eq(lesson_progress.user_id, userId), eq(lesson_progress.course_id, courseId)));
}

/**
 * Progreso del alumno en TODOS sus cursos con el título de cada lección (para la
 * cola de repaso SRS). Local-first: `lesson_progress` y `lesson` ya llegan por
 * los streams de aprendizaje; el join se resuelve en la réplica local.
 */
export function getMyReviewProgressQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select({
      lesson_id: lesson_progress.lesson_id,
      course_id: lesson_progress.course_id,
      status: lesson_progress.status,
      score: lesson_progress.score,
      attempts: lesson_progress.attempts,
      completed_at: lesson_progress.completed_at,
      updated_at: lesson_progress.updated_at,
      lesson_title: lesson.title,
    })
    .from(lesson_progress)
    .innerJoin(lesson, eq(lesson_progress.lesson_id, lesson.id))
    .where(eq(lesson_progress.user_id, userId));
}

/** Stats de gamificación server-authoritative del alumno (XP/racha). */
export function getLearnerStatsQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db.select().from(learner_stats).where(eq(learner_stats.user_id, userId)).limit(1);
}

/**
 * Leaderboard de una liga (`'global'` o el id de una organización). SOLO llegan
 * entradas con `opted_in = true` (el servidor filtra por consentimiento en el
 * stream), así que quien no opta NO aparece aquí. El ranking/tiers los calcula
 * la vista con el core `gamification/leagues.ts` (rankLeaderboard + assignTier).
 *
 * **D10 · la cota ya no vive aquí.** Esta consulta tenía un `.limit(100)` que
 * era el ÚNICO tope del sistema: el stream replicaba la liga entera y el cliente
 * tiraba el resto. Ahora el servidor materializa el puesto en `league_rank`
 * (migración 00083) y el stream solo entrega `league_rank <= 100`, así que lo
 * que llega ya está acotado y un `.limit()` aquí sería un segundo sitio
 * decidiendo lo mismo. Se ordena por `league_rank` — el orden que el servidor
 * ya calculó — en vez de recalcularlo por XP.
 */
export function getLeaderboardQuery(db: PowerSyncDrizzleDB, leagueId: string) {
  return db
    .select()
    .from(leaderboard_entry)
    .where(eq(leaderboard_entry.league_id, leagueId))
    .orderBy(leaderboard_entry.league_rank);
}

/**
 * ¿El invitado (guest) tiene datos de APRENDIZAJE locales que valga la pena
 * migrar a la cuenta real al registrarse? Cuenta sus inscripciones — en Space
 * guest-first, un invitado consume cursos offline y acumula enrollment/progreso
 * antes de crear cuenta. Reemplaza el antiguo conteo de tareas (`todo`) del
 * canon Space, ya contraído. Usado por el prompt "¿conservar datos locales?".
 */
export function getLocalLearningDataCountQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db.select({ total: count() }).from(enrollment).where(eq(enrollment.user_id, userId));
}
